/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
/**
 * AnticipateHome — the landing/home page (/home, gated on modules.newHomeDashboard).
 *
 * Finalised redesign (Qantas, Oct 2026). Surfaces ONLY four things over the fixed L2 taxonomy, in
 * strict priority order:
 *   1. Watchlist risks AT/ABOVE worst case            (red warning triangle; red border + bold cell)
 *   2. Watchlist risks TRENDING toward worst case      (amber warning triangle)
 *   3. Watchlist risks at/above OR trending to best     (green circled plus; opportunity side only)
 *   4. ACTIVE P1 emerging themes (recent alerts)        (blue info; tagged "on watchlist" when
 *      their L2 is already tracked by a risk, so a strong P1 isn't hidden behind a quiet risk)
 * Everything else (risks comfortably mid-range, dormant or below-P1 themes) is hidden.
 *
 * Four summary boxes (Read more → filters the grid + tables), a priority-sorted taxonomy grid with
 * hover pop-ups, two toggles (hide empty categories / show risk appetite — both default off), two
 * linked summary tables underneath, and a "View Risk Appetite" PDF export.
 *
 * Live data: GET /taxonomy/list (L1/L2 names + per-L2 Appetite + definitions),
 *   GET /emergingRisk/radar-data?includeDrafts=true (risks + proximity + AI narrative + scenarios),
 *   GET /anticipateHorizonScan/v2/model (emerging themes + priority band),
 *   GET /appetite-levels/config (appetite level titles/colours + definitions, for the PDF + bands).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import InfoIcon from "@mui/icons-material/Info";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";

const API = protectedResources?.apiTodoList?.endpoint || "";
const READ_KEY = "horizonV2_readThemes";

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// ID139 — legacy/variant appetite value (lowercased) → the default level-title bucket, so an L2 whose
// stored Appetite is e.g. "Opportunity" still matches the configured level "Opportunity Seeking".
const APPETITE_TITLE_ALIAS = {
  "risk aversion": "risk aversion",
  "risk averse": "risk aversion",
  "balanced risk aversion": "balanced risk aversion",
  "risk balanced": "balanced risk aversion",
  "balanced opportunity": "balanced opportunity",
  opportunity: "opportunity seeking",
  "opportunity seeking": "opportunity seeking",
  "risk seeking": "opportunity seeking",
};

// The four buckets, in display/priority order. `rank` drives column + cell sorting (lower = first).
const BUCKETS = {
  worst: {
    rank: 1,
    color: "#c62828",
    label: "At/above worst case",
    tileLabel: "Red alert Watchlist risk",
    text: "Watchlist risk triggered worst case",
    Icon: WarningIcon,
  },
  trending: {
    rank: 2,
    color: "#ef6c00",
    label: "Trending toward worst case",
    tileLabel: "Amber alert Watchlist risk",
    text: "Watchlist risk trending toward worst case",
    Icon: WarningAmberIcon,
  },
  best: {
    rank: 3,
    color: "#2e9e4f",
    label: "At/above or trending to best case",
    tileLabel: "Green alert Watchlist risk",
    text: "Watchlist risk triggered or trending toward best case",
    Icon: AddCircleIcon,
  },
  theme: {
    rank: 4,
    color: "#1565c0",
    label: "P1 emerging theme",
    tileLabel: "Priority 1 emerging themes",
    text: "Priority 1 theme emerging, not on your watchlist",
    textOnWatch: "Priority 1 theme emerging, on your watchlist",
    Icon: InfoIcon,
  },
};
const BUCKET_ORDER = ["worst", "trending", "best", "theme"];

const HOVER_SHADOW = "0 4px 12px rgba(82,0,155,0.22)";

// Classify a watchlist risk into bucket 1/2/3 using POSITION (signpostProximity: 0=worst→1=best) AND
// DIRECTION (overallSignalTrend.direction). Thresholds are the PO-agreed defaults.
// NB: a red/triggered signpost does NOT imply "worst" — a signpost can breach toward the BEST case too
// (opportunity threshold). The position (prox) already reflects that, incl. the triggered→near-worst
// fallback for risks with no overall signal, so we classify on position + direction only (ID212 fix).
function classifyRisk(r) {
  if (r.draft) return null;
  const p = r.prox; // 0 = at worst, 1 = at best
  if (p == null) return null;
  // 1 — at/above worst: at the worst end and not actively improving away from it.
  if (p <= 0.15 && r.dir !== "improving") return "worst";
  // 2 — trending toward worst: in the worsening half and the overall signal is worsening.
  if (p <= 0.55 && r.dir === "worsening") return "trending";
  // 3 — at/above or trending to best (opportunity side only).
  if (r.bestPositive && (p >= 0.6 || r.dir === "improving")) return "best";
  return null;
}

// ID214 — the risk's position between worst/best as a %, toward the NEARER pole ("74% to best case").
function posLabel(r) {
  if (r.prox == null) return "—";
  const pctBest = Math.round(r.prox * 100);
  return r.prox >= 0.5 ? `${pctBest}% to best case` : `${100 - pctBest}% to worst case`;
}
const towardBest = (r) => r.prox != null && r.prox >= 0.5;

const firstSentence = (txt) => {
  const s = String(txt || "").trim();
  if (!s) return "";
  const m = s.match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : s).trim();
};

export default function AnticipateHome() {
  const navigate = useNavigate();
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const [taxonomy, setTaxonomy] = useState([]); // [{ l1, l1Def, l2:[{ name, appetite, def }] }]
  const [risks, setRisks] = useState([]);
  const [themes, setThemes] = useState([]);
  const [appetite, setAppetite] = useState({ levels: [], criteria: [], complianceStatement: "" });
  const [loading, setLoading] = useState(true);

  const [hideEmpty, setHideEmpty] = useState(true); // "Hide empty categories" — default ON
  const [showAppetite, setShowAppetite] = useState(false); // "Show risk appetite" — default OFF
  const [filter, setFilter] = useState(null); // one of BUCKET_ORDER, from a "Read more" box
  const [highlight, setHighlight] = useState(null); // { l1, l2 } clicked cell → highlight table rows

  const [readMap, setReadMap] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem(READ_KEY) || "{}") || {};
    } catch {
      return {};
    }
  });
  const tablesRef = useRef(null);

  const markRead = useCallback(
    (t) => {
      if (!t?.theme) return;
      const next = { ...readMap, [t.theme]: t.contentVersion || 1 };
      setReadMap(next);
      try {
        window.localStorage.setItem(READ_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [readMap]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const run = executeRef.current;
    const [taxRes, riskRes, modelRes, appRes] = await Promise.all([
      run("GET", `${API}/taxonomy/list`).catch(() => null),
      run("GET", `${API}/emergingRisk/radar-data?includeDrafts=true`).catch(() => null),
      run("GET", `${API}/anticipateHorizonScan/v2/model`).catch(() => null),
      run("GET", `${API}/appetite-levels/config`).catch(() => null),
    ]);

    // Taxonomy — pick the published doc (else first). Keep per-L2 appetite + definitions.
    let taxList = [];
    if (Array.isArray(taxRes?.data)) taxList = taxRes.data;
    else if (Array.isArray(taxRes)) taxList = taxRes;
    const taxDoc = taxList.find((t) => t.status === "published") || taxList[0] || null;
    setTaxonomy(
      (taxDoc?.Categories || []).map((c) => ({
        l1: c.name || "",
        l1Def: c.desc || "",
        owner: c.OwnerName || c.Owner || "", // ID210 — show the owner's name, not their email
        l2: (c.SubCategories || []).map((s) => ({
          name: s.name || "",
          appetite: s.Appetite || "",
          def: s.Description || "",
        })),
      }))
    );

    // Risks — classify into buckets; keep scenario values for the "estimated current value".
    const rArr = riskRes?.data || riskRes || [];
    setRisks(
      (Array.isArray(rArr) ? rArr : []).map((r) => {
        const row = {
          id: r.id || r._id,
          t: r.title || r.Title || "Untitled risk",
          l1: r.category || r.Category || "",
          l2: r.subCategory || r.SubCategory || "",
          linkedTheme: r.linkedHorizonTheme || "",
          draft: (r.status || "active") === "draft",
          dir: r.overallSignalTrend?.direction || "stable",
          prox: typeof r.signpostProximity === "number" ? r.signpostProximity : null,
          signpostDirection: r.signpostDirection || "",
          signpostAlert: r.signpostAlert || "none",
          bestPositive: r.scenarios?.optimistic?.threatOpp === "opportunity",
          optVal: Number(r.scenarios?.optimistic?.value || 0),
          pessVal: Number(r.scenarios?.pessimistic?.value || 0),
          optOpp: r.scenarios?.optimistic?.threatOpp || "threat",
          pessOpp: r.scenarios?.pessimistic?.threatOpp || "threat",
          desc: r.description || r.Description || "",
          narrative: r.overallSignalTrend?.narrative || "",
        };
        row.bucket = classifyRisk(row);
        return row;
      })
      // Keep ALL risks (even unbucketed/mid-range) so coveredL2 knows every theme already on a
      // watchlist; display paths below gate on `bucket` so only the 3 buckets ever show.
    );

    // Themes — the P1 SUB-THEMES (the actual emerging stories, e.g. "Qantas expands international
    // routes"), each with its own alert count. `theme` holds the parent L2 category (for grouping,
    // read/unread, navigation and the table's category column). "Not on a watchlist" is applied later
    // at the L2 level via coveredL2.
    const groups = modelRes?.data?.model?.groups || modelRes?.data?.groups || [];
    const flat = [];
    (groups || []).forEach((g) =>
      (g.themes || []).forEach((t) => {
        (t.subThemes || []).forEach((st) => {
          // Only ACTIVE P1 themes count — one with no recent alerts is dormant, not emerging.
          if (
            st.prioritisation?.priorityBand === 1 &&
            !st.isOther &&
            st.name &&
            (st.recentAlerts || 0) > 0
          ) {
            flat.push({
              name: st.name, // sub-theme (story) name
              theme: t.theme, // parent L2 category
              l1: g.l1,
              recent: st.recentAlerts || 0,
              alertCount: st.alertCount || 0,
              why: t.whyItMatters || t.summary || "",
              contentVersion: t.contentVersion || 1,
            });
          }
        });
      })
    );
    setThemes(flat);

    const appCfg = appRes?.data || null;
    if (appCfg) {
      setAppetite({
        levels: appCfg.levels || [],
        criteria: appCfg.criteria || [],
        complianceStatement: appCfg.complianceStatement || "",
      });
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // L2 → appetite colour, from the configured levels (matched on title).
  const appetiteColor = useCallback(
    (appetiteStr) => {
      const v = norm(appetiteStr);
      if (!v) return null;
      const levels = appetite.levels || [];
      let lvl = levels.find((l) => norm(l.title) === v); // direct title match
      if (!lvl && APPETITE_TITLE_ALIAS[v]) {
        lvl = levels.find((l) => norm(l.title) === APPETITE_TITLE_ALIAS[v]); // legacy/variant alias
      }
      return lvl?.color || null;
    },
    [appetite.levels]
  );

  // Covered L2s (a theme already tracked by a watchlist risk) — used when a theme has no riskCount.
  const coveredL2 = useMemo(() => {
    const s = new Set();
    risks.forEach((r) => {
      s.add(norm(r.l2));
      s.add(norm(r.linkedTheme));
    });
    return s;
  }, [risks]);

  // Bucket 4 — active P1 emerging themes. Themes whose parent L2 is already tracked by a watchlist
  // risk are still shown but flagged `onWatchlist` (so a strong P1 isn't hidden behind a quiet risk).
  const shownThemes = useMemo(
    () => themes.map((t) => ({ ...t, onWatchlist: coveredL2.has(norm(t.theme)) })),
    [themes, coveredL2]
  );

  // Per-cell content, honouring the active "Read more" filter.
  const cellRisks = useCallback(
    (cellL1, cellL2) => {
      if (filter === "theme") return [];
      const k = norm(cellL2);
      return risks.filter(
        (r) =>
          r.bucket &&
          norm(r.l1) === norm(cellL1) &&
          norm(r.l2) === k &&
          (!filter || r.bucket === filter)
      );
    },
    [risks, filter]
  );
  const cellThemes = useCallback(
    (cellL1, cellL2) => {
      if (filter && filter !== "theme") return [];
      const k = norm(cellL2);
      return shownThemes.filter((t) => norm(t.l1) === norm(cellL1) && norm(t.theme) === k);
    },
    [shownThemes, filter]
  );

  // Rank for sorting: most-severe bucket present in a cell / column (lower = first).
  const cellRank = useCallback(
    (cellL1, cellL2) => {
      let r = 99;
      cellRisks(cellL1, cellL2).forEach((x) => {
        r = Math.min(r, BUCKETS[x.bucket].rank);
      });
      if (cellThemes(cellL1, cellL2).length) r = Math.min(r, BUCKETS.theme.rank);
      return r;
    },
    [cellRisks, cellThemes]
  );

  // Ordered columns: each L1 with its L2 cells; columns sorted by most-severe content then count.
  const columns = useMemo(() => {
    const cols = taxonomy.map((g) => {
      const cells = g.l2.map((l2) => {
        const rs = cellRisks(g.l1, l2.name);
        const ts = cellThemes(g.l1, l2.name);
        return { ...l2, l1: g.l1, risks: rs, themes: ts, rank: cellRank(g.l1, l2.name) };
      });
      const active = cells.filter((c) => c.risks.length + c.themes.length > 0);
      const rank = active.length ? Math.min(...active.map((c) => c.rank)) : 99;
      const count = active.reduce((a, c) => a + c.risks.length + c.themes.length, 0);
      // cells sorted by urgency, active first
      cells.sort((a, b) => a.rank - b.rank);
      return { l1: g.l1, owner: g.owner, cells, active, rank, count };
    });
    const visible = hideEmpty ? cols.filter((c) => c.active.length > 0) : cols;
    return visible.sort((a, b) => a.rank - b.rank || b.count - a.count || a.l1.localeCompare(b.l1));
  }, [taxonomy, cellRisks, cellThemes, cellRank, hideEmpty]);

  // Rows for the two bottom tables (only what's on the home page), honouring filter + highlight.
  const tableRisks = useMemo(() => {
    let rows = risks.filter((r) => r.bucket);
    if (filter === "theme") return [];
    if (filter) rows = rows.filter((r) => r.bucket === filter);
    return rows.sort((a, b) => BUCKETS[a.bucket].rank - BUCKETS[b.bucket].rank);
  }, [risks, filter]);
  const tableThemes = useMemo(() => {
    if (filter && filter !== "theme") return [];
    return shownThemes.slice().sort((a, b) => b.recent - a.recent);
  }, [shownThemes, filter]);

  const isHot = useCallback(
    (l1, l2) => highlight && norm(highlight.l1) === norm(l1) && norm(highlight.l2) === norm(l2),
    [highlight]
  );

  const onCellClick = (l1, l2) => {
    setHighlight((h) =>
      h && norm(h.l1) === norm(l1) && norm(h.l2) === norm(l2) ? null : { l1, l2 }
    );
    if (tablesRef.current) tablesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const openRisk = (r) => navigate("/anticipate/EmergingRisks", { state: { openRiskId: r.id } });
  // Open the theme's DETAIL on the Horizon Scan dashboard (not the watchlist form).
  const openTheme = (t) => {
    markRead(t);
    navigate("/anticipate/HorizonScan", { state: { openThemeL2: t.theme } });
  };

  // Top four priority boxes.
  const boxes = useMemo(() => {
    const byBucket = (k) => risks.filter((r) => r.bucket === k);
    return [
      { key: "worst", ...BUCKETS.worst, items: byBucket("worst").map((r) => r.t) },
      { key: "trending", ...BUCKETS.trending, items: byBucket("trending").map((r) => r.t) },
      { key: "best", ...BUCKETS.best, items: byBucket("best").map((r) => r.t) },
      {
        key: "theme",
        ...BUCKETS.theme,
        label: "Priority 1 emerging themes",
        items: shownThemes.map((t) => t.name),
      },
    ];
  }, [risks, shownThemes]);

  // Hover pop-up content for a cell.
  const cellTooltip = (cell) => (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      {cell.risks.map((r) => {
        const B = BUCKETS[r.bucket];
        const sentence = firstSentence(r.narrative);
        return (
          <Box key={r.id} sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 12.5,
                display: "flex",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              <B.Icon sx={{ fontSize: 15, color: B.color }} /> {r.t}
            </Typography>
            {r.desc ? (
              <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.85)" }}>
                {r.desc}
              </Typography>
            ) : null}
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                color: towardBest(r) ? "#8ef5b0" : "#ff9e9e",
              }}
            >
              Current state: {posLabel(r)}
            </Typography>
            {sentence ? (
              <Typography
                sx={{ fontSize: 11, fontStyle: "italic", color: "rgba(255,255,255,0.8)" }}
              >
                {sentence}
              </Typography>
            ) : null}
          </Box>
        );
      })}
      {cell.themes.map((t) => (
        <Box
          key={`${t.theme}-${t.name}`}
          sx={{ display: "flex", gap: 0.5, alignItems: "flex-start", mb: 0.4 }}
        >
          <InfoIcon sx={{ fontSize: 15, color: BUCKETS.theme.color, mt: "1px", flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
            {t.name}
            <Box component="span" sx={{ fontWeight: 400, color: "rgba(255,255,255,0.8)" }}>
              {" "}
              · {t.recent} alerts
            </Box>
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const ink = "#344767";
  const muted = "#7b809a";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ px: 2.5, pt: 2.5, pb: 8, color: ink }}>
        {/* "View Risk Appetite" now lives in the top nav (beside Generate Report), on every screen. */}

        {/* Four priority boxes (Read more → filter) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
            gap: 1.5,
            mb: 2,
          }}
        >
          {boxes.map((b) => {
            const on = filter === b.key;
            const dimmed = filter && !on;
            return (
              <Card
                key={b.key}
                sx={{
                  p: 1.25,
                  borderTop: `3px solid ${b.color}`,
                  opacity: dimmed ? 0.5 : 1,
                  outline: on ? `2px solid ${b.color}` : "none",
                  boxShadow: "0 1px 2px rgba(26,21,38,.06), 0 6px 20px rgba(26,21,38,.05)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <b.Icon sx={{ color: b.color, fontSize: 20 }} />
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 800,
                      lineHeight: 1,
                      color: b.color,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {b.items.length}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ink, lineHeight: 1.1 }}>
                    {b.tileLabel}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: muted,
                    mt: 0.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {b.items.join(", ") || "None right now"}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setFilter(on ? null : b.key)}
                  sx={{
                    mt: 0.5,
                    p: 0,
                    minWidth: 0,
                    color: b.color,
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 12,
                  }}
                >
                  {on ? "✕ Clear" : "Read more"}
                </Button>
              </Card>
            );
          })}
        </Box>

        {/* Toggles + legend — one compact row */}
        <Card
          sx={{
            px: 1.5,
            py: 0.75,
            mb: 2,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            columnGap: 2,
            rowGap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch
              size="small"
              checked={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
            />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ink }}>
              Hide empty categories
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch
              size="small"
              checked={showAppetite}
              onChange={(e) => setShowAppetite(e.target.checked)}
            />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ink }}>
              Show risk appetite
            </Typography>
          </Box>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              columnGap: 1.5,
              rowGap: 0.25,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {BUCKET_ORDER.map((k) => {
              const B = BUCKETS[k];
              return (
                <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <B.Icon sx={{ fontSize: 15, color: B.color }} />
                  <Typography sx={{ fontSize: 10.5, color: muted }}>{B.label}</Typography>
                </Box>
              );
            })}
          </Box>
        </Card>

        {/* Taxonomy grid */}
        {loading && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={26} sx={{ color: "#52009b" }} />
          </Box>
        )}
        {!loading && columns.length === 0 && (
          <Box sx={{ color: muted, py: 5, fontSize: 14 }}>
            Nothing needs attention right now. Turn off <b>Hide empty categories</b> to see the
            whole taxonomy.
          </Box>
        )}
        {!loading && columns.length > 0 && (
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start", pb: 1.5 }}
          >
            {columns.map((c) => (
              <Box
                key={c.l1}
                sx={{
                  flex: "1 0 150px",
                  minWidth: 150,
                  border: "1px solid rgba(82,0,155,0.15)",
                  borderRadius: 1,
                  p: 0.5,
                  bgcolor: "#faf9fc",
                }}
              >
                {/* L1 header — name + owner. No left colour: the left band is appetite, on L2 only. */}
                <Box
                  sx={{
                    bgcolor: "#ede7f6",
                    border: "1px solid #ce93d8",
                    borderRadius: 1,
                    px: 1,
                    mb: 0.5,
                    height: 52,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 11.5,
                      color: "#52009b",
                      lineHeight: 1.15,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {c.l1}
                  </Typography>
                  {typeof c.owner === "string" && c.owner ? (
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "#999",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.owner}
                    </Typography>
                  ) : null}
                </Box>

                {/* L2 cells — icon + text per applicable bucket; left band = appetite (toggle, off by default) */}
                {(hideEmpty ? c.active : c.cells).map((cell) => {
                  const empty = cell.risks.length + cell.themes.length === 0;
                  const hasWorst = cell.risks.some((r) => r.bucket === "worst");
                  const aColor = showAppetite ? appetiteColor(cell.appetite) : null;
                  // Every applicable bucket (risk buckets + P1 theme), in priority order 1→4.
                  const marks = BUCKET_ORDER.filter((k) =>
                    k === "theme" ? cell.themes.length : cell.risks.some((r) => r.bucket === k)
                  );
                  const inner = (
                    <Box
                      role="button"
                      tabIndex={empty ? -1 : 0}
                      onClick={empty ? undefined : () => onCellClick(cell.l1, cell.name)}
                      onKeyDown={
                        empty
                          ? undefined
                          : (e) =>
                              (e.key === "Enter" || e.key === " ") &&
                              onCellClick(cell.l1, cell.name)
                      }
                      sx={{
                        border: hasWorst ? "1.5px solid #c62828" : "1px solid #e7e2ee",
                        borderLeft: aColor ? `5px solid ${aColor}` : undefined,
                        borderRadius: 1,
                        p: 0.6,
                        mb: 0.5,
                        background: empty ? "transparent" : "#fff",
                        cursor: empty ? "default" : "pointer",
                        opacity: empty ? 0.55 : 1,
                        outline: isHot(cell.l1, cell.name) ? "2px solid #52009b" : "none",
                        transition: "box-shadow .12s",
                        "&:hover": empty ? {} : { boxShadow: HOVER_SHADOW },
                      }}
                    >
                      <Typography
                        sx={{
                          // At/above-worst: same colour as other category names, just BOLD (ID-home).
                          fontWeight: hasWorst ? 800 : 500,
                          fontSize: 10.5,
                          color: ink,
                          lineHeight: 1.2,
                          wordBreak: "break-word",
                        }}
                      >
                        {cell.name}
                      </Typography>
                      {marks.map((k) => {
                        const B = BUCKETS[k];
                        // P1-theme cell: wording depends on whether the L2 is already on a watchlist.
                        const markText =
                          k === "theme" && cell.themes[0]?.onWatchlist ? B.textOnWatch : B.text;
                        return (
                          <Box
                            key={k}
                            sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}
                          >
                            <B.Icon
                              sx={{ fontSize: 15, color: B.color, flexShrink: 0, mt: "1px" }}
                            />
                            <Typography
                              sx={{
                                fontSize: 9.5,
                                fontWeight: 600,
                                color: B.color,
                                lineHeight: 1.2,
                              }}
                            >
                              {markText}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                  return empty ? (
                    <Box key={cell.name}>{inner}</Box>
                  ) : (
                    <Tooltip key={cell.name} arrow placement="right" title={cellTooltip(cell)}>
                      {inner}
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}

        {/* Two summary tables */}
        <Box
          ref={tablesRef}
          sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, gap: 2, mt: 3 }}
        >
          <Card sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#52009b", mb: 1 }}>
              Watchlist risk summary
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: muted }}>Risk</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: muted }}>
                    Current State
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRisks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ color: muted, fontSize: 12 }}>
                      No watchlist risks to show.
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRisks.map((r) => {
                    const B = BUCKETS[r.bucket];
                    const hot = isHot(r.l1, r.l2);
                    return (
                      <TableRow
                        key={r.id}
                        hover
                        onClick={() => openRisk(r)}
                        sx={{
                          cursor: "pointer",
                          background: hot ? "rgba(82,0,155,0.07)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ fontSize: 12 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <B.Icon sx={{ fontSize: 16, color: B.color }} />
                            <span style={{ fontWeight: 600 }}>{r.t}</span>
                          </Box>
                          <Typography sx={{ fontSize: 10.5, color: muted }}>
                            {firstSentence(r.narrative)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: towardBest(r) ? "#2e7d32" : "#c62828",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {posLabel(r)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>

          <Card sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#52009b", mb: 1 }}>
              Priority 1 emerging themes
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: muted }}>Theme</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: muted }}>
                    Category
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableThemes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ color: muted, fontSize: 12 }}>
                      No active P1 themes.
                    </TableCell>
                  </TableRow>
                ) : (
                  tableThemes.map((t) => {
                    const hot = isHot(t.l1, t.theme);
                    return (
                      <TableRow
                        key={`${t.theme}-${t.name}`}
                        hover
                        onClick={() => openTheme(t)}
                        sx={{
                          cursor: "pointer",
                          background: hot ? "rgba(82,0,155,0.07)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ fontSize: 12 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 16, color: BUCKETS.theme.color }} />
                            <span style={{ fontWeight: 600 }}>{t.name}</span>
                            <span style={{ color: muted }}>· {t.recent} alerts</span>
                            {t.onWatchlist && (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 9.5,
                                  fontWeight: 700,
                                  color: BUCKETS.theme.color,
                                  border: `1px solid ${BUCKETS.theme.color}`,
                                  borderRadius: "10px",
                                  px: 0.7,
                                  py: 0.05,
                                  lineHeight: 1.4,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                }}
                              >
                                On watchlist
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11.5, color: muted }}>{t.theme}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 11, color: muted }}>
          Showing only: watchlist risks at/above or trending to their worst or best case, and active
          Priority 1 emerging themes (tagged &quot;on watchlist&quot; when their category is already
          tracked by a risk). Click a category to highlight its rows below; click a row to open the
          full record.
        </Typography>
      </Box>
    </DashboardLayout>
  );
}
